import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import UserLayout from '../../components/user/UserLayout';
import toast from 'react-hot-toast';
import { Bitcoin, Lock, TrendingUp, Upload, CheckCircle, Clock, XCircle, Wallet } from 'lucide-react';

export default function InvestPage() {
  const [packages, setPackages] = useState([]);
  const [myInvestments, setMyInvestments] = useState([]);
  const [investWallet, setInvestWallet] = useState(0);
  const [investWalletDaily, setInvestWalletDaily] = useState(0);
  const [investWalletWeekly, setInvestWalletWeekly] = useState(0);
  const [investWalletMonthly, setInvestWalletMonthly] = useState(0);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('packages');

  const [paymentConfig, setPaymentConfig] = useState(null);
  const [investmentMinWithdraw, setInvestmentMinWithdraw] = useState(50);
  const [mainWallet, setMainWallet] = useState(0);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawOtpSent, setWithdrawOtpSent] = useState(false);
  const [withdrawOtpCode, setWithdrawOtpCode] = useState('');
  const [savedWithdrawalDetails, setSavedWithdrawalDetails] = useState(null);
  const [withdrawMessage, setWithdrawMessage] = useState('');
  const [withdrawMethod, setWithdrawMethod] = useState('bank');
  const [withdrawAccountHolderName, setWithdrawAccountHolderName] = useState('');
  const [withdrawBankName, setWithdrawBankName] = useState('');
  const [withdrawAccountNumber, setWithdrawAccountNumber] = useState('');
  const [withdrawIfscCode, setWithdrawIfscCode] = useState('');
  const [withdrawUpiId, setWithdrawUpiId] = useState('');
  const [withdrawCryptoAddress, setWithdrawCryptoAddress] = useState('');
  const [withdrawCryptoNetwork, setWithdrawCryptoNetwork] = useState('TRC20');

  const fetchAll = async () => {
    const [pkgRes, invRes, walRes, balRes, cfgRes, savedRes] = await Promise.all([
      axios.get('/api/investment/packages'),
      axios.get('/api/investment/my-investments'),
      axios.get('/api/investment/wallet-balance'),
      axios.get('/api/wallet/balance').catch(() => ({ data: {} })),
      axios.get('/api/payment/admin-payment-config').catch(() => ({ data: {} })),
      axios.get('/api/withdrawal/saved-details').catch(() => ({ data: { withdrawalDetails: null } })),
    ]);
    setPackages(pkgRes.data.packages || []);
    setMyInvestments(invRes.data.investments || []);
    setInvestWallet(walRes.data.investmentWalletBalance || 0);
    setInvestWalletDaily(walRes.data.investmentWalletBalanceDaily || 0);
    setInvestWalletWeekly(walRes.data.investmentWalletBalanceWeekly || 0);
    setInvestWalletMonthly(walRes.data.investmentWalletBalanceMonthly || 0);
    setMainWallet(balRes.data.mainWallet || 0);
    setPaymentConfig(cfgRes.data);
    setInvestmentMinWithdraw(cfgRes.data.minInvestmentWithdrawal || 50);
    const savedDetails = savedRes.data.withdrawalDetails || null;
    setSavedWithdrawalDetails(savedDetails);
    if (savedDetails) {
      setWithdrawMethod(savedDetails.method || 'bank');
      setWithdrawAccountHolderName(savedDetails.accountHolderName || '');
      setWithdrawBankName(savedDetails.bankName || '');
      setWithdrawAccountNumber(savedDetails.accountNumber || '');
      setWithdrawIfscCode(savedDetails.ifscCode || '');
      setWithdrawUpiId(savedDetails.upiId || '');
      setWithdrawCryptoAddress(savedDetails.cryptoAddress || '');
      setWithdrawCryptoNetwork(savedDetails.cryptoNetwork || 'TRC20');
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handlePurchase = async () => {
    if (!selectedPkg) return;
    if (mainWallet < selectedPkg.price) return toast.error('Please add balance on your main wallet.');
    setLoading(true);
    try {
      await axios.post('/api/investment/purchase', { packageId: selectedPkg._id });
      toast.success('Investment successful! Package activated.');
      setSelectedPkg(null);
      fetchAll();
      setTab('my-investments');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Please add balance on your main wallet.');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    const amountValue = parseFloat(withdrawAmount);
    if (!withdrawAmount || !Number.isFinite(amountValue) || amountValue <= 0) return toast.error('Please enter a valid amount.');
    if (amountValue < investmentMinWithdraw) return toast.error(`Minimum investment withdrawal is $${investmentMinWithdraw}`);
    setWithdrawLoading(true);
    try {
      if (!withdrawOtpSent) {
        const method = withdrawMethod || savedWithdrawalDetails?.method;
        const accountHolderName = withdrawAccountHolderName || savedWithdrawalDetails?.accountHolderName || '';
        const bankName = withdrawBankName || savedWithdrawalDetails?.bankName || '';
        const accountNumber = withdrawAccountNumber || savedWithdrawalDetails?.accountNumber || '';
        const ifscCode = withdrawIfscCode || savedWithdrawalDetails?.ifscCode || '';
        const upiId = withdrawUpiId || savedWithdrawalDetails?.upiId || '';
        const cryptoAddress = withdrawCryptoAddress || savedWithdrawalDetails?.cryptoAddress || '';
        const cryptoNetwork = withdrawCryptoNetwork || savedWithdrawalDetails?.cryptoNetwork || 'TRC20';

        if (!method) {
          return toast.error('Please select a payment method.');
        }
        if (method === 'bank' && (!accountHolderName || !bankName || !accountNumber || !ifscCode)) {
          return toast.error('Please fill all bank details before requesting withdrawal.');
        }
        if (method === 'upi' && !upiId) {
          return toast.error('Please enter your UPI ID.');
        }
        if (method === 'crypto' && !cryptoAddress) {
          return toast.error('Please enter your crypto wallet address.');
        }

        const payload = {
          amount: withdrawAmount,
          walletType: 'investment',
          method,
          bankName,
          accountNumber,
          ifscCode,
          accountHolderName,
          upiId,
          cryptoAddress,
          cryptoNetwork,
          saveDetails: false,
        };
        const res = await axios.post('/api/withdrawal/request', payload);
        setWithdrawOtpSent(true);
        setWithdrawMessage(res.data.message || 'OTP sent to your email. Enter it below to confirm the investment withdrawal.');
      } else {
        if (!withdrawOtpCode.trim()) return toast.error('Please enter the OTP sent to your email.');
        const res = await axios.post('/api/withdrawal/request', {
          otp: withdrawOtpCode.trim(),
          amount: withdrawAmount,
          walletType: 'investment',
        });
        toast.success(res.data.message || 'Investment withdrawal request submitted successfully!');
        setWithdrawAmount('');
        setWithdrawOtpSent(false);
        setWithdrawOtpCode('');
        setWithdrawMessage('');
        fetchAll();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setWithdrawLoading(false);
    }
  };

  const statusBadge = {
    pending: 'bg-amber-500/20 text-amber-300',
    active: 'bg-primary-500/20 text-primary-300',
    completed: 'bg-blue-500/20 text-blue-300',
    rejected: 'bg-red-500/20 text-red-300'
  };

  return (
    <UserLayout>
      <div className="animate-fade-in">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Bitcoin className="w-7 h-7 text-amber-400" /> Fund Investor
            </h1>
            <p className="text-gray-400 mt-1">Invest in packages and earn monthly returns</p>
          </div>
          <div className="bg-dark-700 border border-white/5 rounded-2xl px-5 py-3 flex items-center gap-3">
            <Wallet className="w-5 h-5 text-amber-400" />
            <div>
              <p className="text-gray-400 text-xs">Investment Wallet</p>
              <p className="text-white font-bold">${investWallet.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab('packages')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${tab === 'packages' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-gray-400 border border-white/5'}`}>
            Available Packages
          </button>
          <button onClick={() => setTab('my-investments')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${tab === 'my-investments' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-gray-400 border border-white/5'}`}>
            My Investments ({myInvestments.length})
          </button>
        </div>

        {/* Package Cards */}
        {tab === 'packages' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {packages.length === 0 ? (
              <div className="col-span-3 text-center py-16">
                <Bitcoin className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">No investment packages available right now</p>
              </div>
            ) : packages.map(pkg => (
              <div key={pkg._id} className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden hover:border-amber-500/30 transition-all">
                <div className="bg-gradient-to-br from-amber-500/20 to-orange-600/10 p-5 border-b border-white/5">
                  <div className="flex items-center justify-between mb-3">
                    <Bitcoin className="w-8 h-8 text-amber-400" />
                    <span className="bg-amber-500/20 text-amber-300 text-xs px-2.5 py-1 rounded-full font-semibold">
                      Active
                    </span>
                  </div>
                  <h3 className="text-white font-bold text-xl mb-1">{pkg.name}</h3>
                  {pkg.description && <p className="text-gray-400 text-sm">{pkg.description}</p>}
                </div>

                <div className="p-5 space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-gray-400 text-sm flex items-center gap-1.5">
                      <TrendingUp className="w-4 h-4" /> Investment Amount
                    </span>
                    <span className="text-white font-bold text-lg">${pkg.price.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-gray-400 text-sm flex items-center gap-1.5">
                      <Lock className="w-4 h-4" /> Lock Duration
                    </span>
                    <span className="text-white font-semibold">{pkg.lockingMonths} Months</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-gray-400 text-sm">Return Rate</span>
                    <span className="text-primary-400 font-bold text-lg">Up to {pkg.currentReturnPercent ?? pkg.monthlyReturnPercent}%</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-gray-400 text-sm">Return Frequency</span>
                    <span className="text-white font-semibold capitalize">{pkg.returnFrequency || 'monthly'}</span>
                  </div>

                  <div className="bg-dark-600 rounded-xl p-3 text-xs text-gray-400">
                    <p>Estimated payout: <span className="text-primary-400 font-semibold">${(pkg.price * (pkg.currentReturnPercent ?? pkg.monthlyReturnPercent) / 100).toFixed(2)}</span> per {pkg.returnFrequency || 'month'}.</p>
                    <p className="mt-0.5 text-gray-500">Principal locked for {pkg.lockingMonths} months. Returns withdrawable according to frequency.</p>
                  </div>

                  <button onClick={() => setSelectedPkg(pkg)}
                    className="w-full bg-amber-600 hover:bg-amber-500 text-white font-semibold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2">
                    <Bitcoin className="w-4 h-4" /> Purchase Package
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* My Investments */}
        {tab === 'my-investments' && (
          <div className="space-y-4">
            {/* Withdraw from investment wallet */}
            <div className="bg-gradient-to-br from-primary-600/20 to-green-600/10 border border-primary-500/30 rounded-2xl p-5 flex flex-col gap-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-gray-400 text-sm">Investment Wallet Balance</p>
                  <p className="text-2xl font-bold text-primary-400">${investWallet.toFixed(2)}</p>
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm w-full md:w-auto">
                  <div className="bg-dark-600 rounded-xl p-3">
                    <p className="text-gray-400 text-xs">Daily</p>
                    <p className="text-white font-semibold">${investWalletDaily.toFixed(2)}</p>
                  </div>
                  <div className="bg-dark-600 rounded-xl p-3">
                    <p className="text-gray-400 text-xs">Weekly</p>
                    <p className="text-white font-semibold">${investWalletWeekly.toFixed(2)}</p>
                  </div>
                  <div className="bg-dark-600 rounded-xl p-3">
                    <p className="text-gray-400 text-xs">Monthly</p>
                    <p className="text-white font-semibold">${investWalletMonthly.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 items-center">
                <div className="flex items-center gap-2">
                  <input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)}
                    className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm w-full focus:outline-none"
                    placeholder="Amount" min="1" max={investWallet} />
                  <button onClick={handleWithdraw} disabled={withdrawLoading}
                    className="bg-primary-600 hover:bg-primary-500 text-white font-semibold px-4 py-2 rounded-xl text-sm flex items-center gap-1.5 transition-colors">
                    {withdrawLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : withdrawOtpSent ? 'Confirm OTP' : 'Withdraw'}
                  </button>
                </div>

                {!withdrawOtpSent && (
                  <div className="bg-dark-700 border border-white/10 rounded-2xl p-4 space-y-3">
                    <p className="text-gray-300 text-sm font-medium">Payment Method</p>
                    <div className="flex flex-wrap gap-2">
                      {['bank', 'upi', 'crypto'].map(m => (
                        <button key={m} type="button" onClick={() => setWithdrawMethod(m)}
                          className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all capitalize ${withdrawMethod === m ? 'border-primary-500 bg-primary-500/20 text-white' : 'border-white/10 bg-dark-600 text-gray-400'}`}>
                          {m === 'bank' ? 'Bank Account' : m === 'upi' ? 'UPI' : 'Crypto'}
                        </button>
                      ))}
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-xs text-gray-400 mb-1">Account Holder Name</label>
                        <input type="text" value={withdrawAccountHolderName} onChange={e => setWithdrawAccountHolderName(e.target.value)}
                          className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm w-full focus:outline-none"
                          placeholder="Account holder name" />
                      </div>

                      {withdrawMethod === 'bank' && (
                        <> 
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Bank Name</label>
                            <input type="text" value={withdrawBankName} onChange={e => setWithdrawBankName(e.target.value)}
                              className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm w-full focus:outline-none"
                              placeholder="Bank name" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Account Number</label>
                            <input type="text" value={withdrawAccountNumber} onChange={e => setWithdrawAccountNumber(e.target.value)}
                              className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm w-full focus:outline-none"
                              placeholder="Account number" />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs text-gray-400 mb-1">IFSC Code</label>
                            <input type="text" value={withdrawIfscCode} onChange={e => setWithdrawIfscCode(e.target.value)}
                              className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm w-full focus:outline-none"
                              placeholder="IFSC code" />
                          </div>
                        </>
                      )}

                      {withdrawMethod === 'upi' && (
                        <div className="md:col-span-2">
                          <label className="block text-xs text-gray-400 mb-1">UPI ID</label>
                          <input type="text" value={withdrawUpiId} onChange={e => setWithdrawUpiId(e.target.value)}
                            className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm w-full focus:outline-none"
                            placeholder="yourname@upi" />
                        </div>
                      )}

                      {withdrawMethod === 'crypto' && (
                        <>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Crypto Network</label>
                            <select value={withdrawCryptoNetwork} onChange={e => setWithdrawCryptoNetwork(e.target.value)}
                              className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm w-full focus:outline-none">
                              <option value="TRC20">TRON (TRC20)</option>
                              <option value="BEP20">BSC (BEP20)</option>
                              <option value="ERC20">Ethereum (ERC20)</option>
                            </select>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs text-gray-400 mb-1">Crypto Wallet Address</label>
                            <input type="text" value={withdrawCryptoAddress} onChange={e => setWithdrawCryptoAddress(e.target.value)}
                              className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm w-full focus:outline-none"
                              placeholder="Wallet address" />
                          </div>
                        </>
                      )}
                    </div>
                    {savedWithdrawalDetails && (
                      <p className="text-xs text-gray-400">
                        Using saved payout details by default. You can change the method or details here before requesting withdrawal.
                      </p>
                    )}
                  </div>
                )}

                {withdrawOtpSent && (
                  <div className="space-y-3">
                    <div className="rounded-2xl bg-dark-600 border border-primary-500/30 p-4 text-sm text-primary-100">
                      {withdrawMessage || 'OTP sent to your email. Enter it below to confirm the investment withdrawal.'}
                    </div>
                    <input type="text" value={withdrawOtpCode} onChange={e => setWithdrawOtpCode(e.target.value)}
                      className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm w-full focus:outline-none"
                      placeholder="Enter OTP" maxLength={6} />
                    <button type="button" onClick={() => {
                        setWithdrawOtpSent(false);
                        setWithdrawOtpCode('');
                        setWithdrawMessage('');
                      }}
                      className="text-xs text-gray-400 hover:text-white underline">
                      Cancel OTP
                    </button>
                  </div>
                )}
                {!withdrawOtpSent && !savedWithdrawalDetails && (
                  <div className="text-xs text-yellow-300">
                    If you don't want to enter details here, save your payout details on the <Link to="/withdrawal" className="text-primary-300 underline">Withdrawal</Link> page.
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400">Minimum investment withdrawal: ${investmentMinWithdraw}. Withdraw from your total investment wallet.</p>
            </div>
            {myInvestments.length === 0 ? (
              <div className="text-center py-16">
                <Bitcoin className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">No investments yet. Browse packages to get started.</p>
              </div>
            ) : myInvestments.map(inv => (
              <div key={inv._id} className="bg-dark-700 border border-white/5 rounded-2xl p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-white font-semibold">{inv.packageName}</p>
                    <p className="text-gray-400 text-sm">${inv.amountInvested.toLocaleString()} invested</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusBadge[inv.status]}`}>
                    {inv.status}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="bg-dark-600 rounded-xl p-3">
                    <p className="text-gray-400 text-xs">Return Rate</p>
                    <p className="text-primary-400 font-semibold">Up to {inv.currentReturnPercent ?? inv.monthlyReturnPercent}%</p>
                  </div>
                  <div className="bg-dark-600 rounded-xl p-3">
                    <p className="text-gray-400 text-xs">Frequency</p>
                    <p className="text-white font-semibold capitalize">{inv.returnFrequency || 'monthly'}</p>
                  </div>
                  <div className="bg-dark-600 rounded-xl p-3">
                    <p className="text-gray-400 text-xs">Total Earned</p>
                    <p className="text-primary-400 font-semibold">${(inv.totalCommissionPaid || 0).toFixed(2)}</p>
                  </div>
                </div>
                {inv.adminNote && <p className="text-gray-500 text-xs mt-2">Note: {inv.adminNote}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Purchase Modal */}
        {selectedPkg && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-dark-700 border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/5">
                <h3 className="text-white font-bold text-lg">Purchase — {selectedPkg.name}</h3>
                <p className="text-gray-400 text-sm mt-0.5">Buy this package directly from your main wallet balance.</p>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-dark-600 rounded-xl p-4 text-sm text-gray-400">
                  <p className="font-semibold text-white mb-2">Purchase from Main Wallet</p>
                  <p>This package purchase will be debited directly from your main wallet balance.</p>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-dark-700 rounded-xl p-3">
                      <p className="text-gray-400">Available</p>
                      <p className="text-white font-semibold">${mainWallet.toFixed(2)}</p>
                    </div>
                    <div className="bg-dark-700 rounded-xl p-3">
                      <p className="text-gray-400">Required</p>
                      <p className="text-white font-semibold">${selectedPkg.price.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {mainWallet < selectedPkg.price && (
                  <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-red-200 text-sm">
                    Please add balance in your main wallet to complete this purchase.
                  </div>
                )}

                <div className="flex gap-3">
                  <button onClick={() => { setSelectedPkg(null); }}
                    className="flex-1 bg-dark-600 border border-white/10 text-gray-300 hover:text-white font-semibold py-3 rounded-xl transition-colors">
                    Cancel
                  </button>
                  <button onClick={handlePurchase} disabled={loading || mainWallet < selectedPkg.price}
                    className="flex-1 bg-amber-600 hover:bg-amber-500 disabled:bg-dark-600 disabled:text-gray-500 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
                    {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      : <><CheckCircle className="w-4 h-4" /> Submit Purchase</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </UserLayout>
  );
}
